import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// /auth/me is the anonymous-probe endpoint — a 401 is the expected response for
// signed-out visitors and should NOT bubble as an error. We swallow it cleanly
// (resolve with a null-user payload) so the console + UI stay quiet.
//
// For any other endpoint, a 401 likely means the short-lived access_token
// has expired. We attempt a one-shot /auth/refresh exchange (uses the long-lived
// refresh_token cookie) and retry the original request. If refresh fails, the
// 401 bubbles to the caller as normal.
let refreshPromise = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config || {};
    const url = original.url || "";
    const status = error?.response?.status;

    if (status === 401 && url.includes("/auth/me")) {
      return Promise.resolve({
        data: { user: null, anonymous: true },
        status: 200,
        statusText: "OK",
        headers: error.response.headers || {},
        config: original,
      });
    }

    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/refresh") || url.includes("/auth/logout");
    if (status === 401 && !isAuthEndpoint && !original.__retried) {
      try {
        if (!refreshPromise) refreshPromise = api.post("/auth/refresh");
        await refreshPromise;
        refreshPromise = null;
        original.__retried = true;
        return api(original);
      } catch (refreshErr) {
        refreshPromise = null;
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fmtUSD(n) {
  if (n === undefined || n === null) return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

export function fmtNum(n) {
  if (n === undefined || n === null) return "—";
  return new Intl.NumberFormat("en-US").format(Math.round(Number(n)));
}

export function fmtRange(arr, formatter = fmtUSD) {
  if (!arr || arr.length < 2) return "—";
  return `${formatter(arr[0])} – ${formatter(arr[1])}`;
}
